import { Component, ViewEncapsulation, forwardRef, ChangeDetectorRef, inject, input, output, linkedSignal, signal, computed } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

/**
 * Interface for radio group options
 */
export interface RadioOption {
  /** The value of the radio option */
  value: string | number | boolean;
  /** The display label for the radio option */
  label: string;
  /** Optional description shown below the label */
  description?: string;
  /** Whether this specific option is disabled */
  disabled?: boolean;
}

/**
 * Radio Group Component
 *
 * A reusable radio group component that implements ControlValueAccessor for use with Angular forms.
 * Supports both reactive and template-driven forms.
 *
 * @example
 * ```html
 * <!-- Reactive Forms -->
 * <app-radio-group
 *   formControlName="method"
 *   [options]="methodOptions"
 *   name="method">
 * </app-radio-group>
 *
 * <!-- Template-driven Forms -->
 * <app-radio-group
 *   [(ngModel)]="selectedValue"
 *   [options]="options"
 *   name="selection">
 * </app-radio-group>
 *
 * <!-- With change event -->
 * <app-radio-group
 *   [(ngModel)]="selectedValue"
 *   [options]="options"
 *   name="selection"
 *   (change)="onSelectionChange($event)">
 * </app-radio-group>
 * ```
 */
@Component({
    selector: 'app-radio-group',
    templateUrl: './radio-group.component.html',
    styleUrls: ['./radio-group.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RadioGroupComponent),
            multi: true
        }
    ],
    imports: [FormsModule]
})
export class RadioGroupComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  /** Array of radio options to display */
  options = input<RadioOption[]>([]);

  /** Whether the entire radio group is disabled */
  disabled = input(false);

  /** Name attribute for the radio inputs (required for proper grouping) */
  name = input('');

  /** Current selected value */
  value = input<string | number | boolean | null>(null);

  /** Event emitted when the selected value changes */
  change = output<string | number | boolean | null>();

  /** Internal disabled state that can be toggled by CVA setDisabledState */
  _disabledByCva = signal(false);

  /** Effective disabled state */
  isDisabled = computed(() => this.disabled() || this._disabledByCva());

  /**
   * Writable signal linked to the `value` input.
   * Follows parent changes but can be independently set by writeValue() (CVA).
   */
  private _value = linkedSignal<string | number | boolean | null, string | number | boolean | null>({
    source: this.value,
    computation: (src) => src ?? null
  });
  private onChange = (value: string | number | boolean | null) => {};
  private onTouched = () => {};

  get internalValue(): string | number | boolean | null {
    return this._value();
  }

  set internalValue(val: string | number | boolean | null) {
    if (val !== this._value()) {
      this._value.set(val);
      this.onChange(val);
      this.cdr.detectChanges();
    }
  }

  writeValue(value: string | number | boolean | null): void {
    this._value.set(value ?? null);
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: string | number | boolean | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
    this.cdr.detectChanges();
  }

  onRadioChange(e: Event): void {
    const target = e.target as HTMLInputElement;
    const selectedValue = this.castValue(target.value);
    this.internalValue = selectedValue;
    this.change.emit(selectedValue);
  }

  onBlur(): void {
    this.onTouched();
  }

  isOptionDisabled(option: RadioOption): boolean {
    return this.isDisabled() || option.disabled || false;
  }

  /**
   * Casts a string value to its original type based on the options
   */
  private castValue(value: string): string | number | boolean {
    const currentOptions = this.options();
    if (!value || currentOptions.length === 0) {
      return value;
    }

    // Find the matching option to determine the original type
    const matchingOption = currentOptions.find(option => String(option.value) === value);

    if (matchingOption) {
      return matchingOption.value;
    }

    // If no matching option found, try to infer type from the first option
    const firstOption = currentOptions[0];
    if (firstOption) {
      const originalType = typeof firstOption.value;

      switch (originalType) {
        case 'number':
          const numValue = Number(value);
          return isNaN(numValue) ? value : numValue;
        case 'boolean':
          return value === 'true';
        default:
          return value;
      }
    }

    return value;
  }
}
